import type { i18n as I18nInstance } from 'i18next';
import { i18n } from '/shared/translations/i18n';

import type { QuestionConfigQuestionType } from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import formatNumber from '/shared/functions/formatNumber';
import getCurrencySymbol from '/shared/functions/getCurrencySymbol';

import robotIcon from '/business/shared/images/icons/bp-robot-arm-pictograms-bp-green-.svg';
import initiativeCardBackground from '/business/shared/images/tip-img-nov2024/Capital Goods card image.jpg';
import initiativeBackground from '/business/shared/images/tip-img-nov2024/Capital Goods card image@2x.jpg';

import useGetCapitalGoodSpendDataSchema from '/business/shared/components/questions/scope3/goods/capitalGoods/_hooks/useGetCapitalGoodSpendDataSchema';
import useFormatSharedScope3Goods from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedScope3GoodsSupplier';
import useFormatSharedScope3GoodsCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedScope3GoodsCustom';

import educationContentJson from '/business/shared/components/questions/scope3/goods/capitalGoods/_education/capitalGoods.json';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import {
  bottomPurchasedCapitalActivity,
  bottomPurchasedCapitalAll,
} from '/business/shared/components/questions/scope3/_education/bottomPurchasedCapital';

export const getConfigureCapitalGoods = (_options: { i18n?: I18nInstance } = {}): QuestionConfigQuestionType => {
  return {
    title: 'Capital goods',
    id: 'capital-goods',
    apiKey: 'calculation-capital-goods-spend-data',
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.capitalGoods',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.capitalGoodsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.capitalGoods',
    scopeCategory: questionScopeCategories.SCOPE_3,
    icon: robotIcon,
    isCompletePropName: 'isCapitalGoodsComplete',
    initiative: {
      getVisuals: () => {
        return {
          description: false,
          background: initiativeBackground,
          useMask: false,
          cardBackground: initiativeCardBackground,
        };
      },
    },
    table: {
      title: i18n.t('business:Questions.calculations.capitalGoods.table.title'),
      totalType: basketTableTotalTypes.TONNAGE,
      headings: [
        i18n.t('business:Questions.calculations.capitalGoods.table.heading1'),
        i18n.t('business:Questions.calculations.capitalGoods.table.heading2'),
        i18n.t('business:Questions.calculations.capitalGoods.table.heading3'),
      ].filter((item) => !!item),
      cells: [
        'spendCategoryLabel',
        ({
          spendValue,
          currencySymbol,
          userLang,
        }: {
          spendValue: number;
          currencySymbol: string;
          userLang: string;
        }) => {
          const currencyStr = getCurrencySymbol({ currencyCode: currencySymbol, userLang });
          return `${currencyStr}${formatNumber(spendValue)}`;
        },
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      tabName: 'Activity data',
      mainQuestionTitle: 'Calculated carbon emissions for capital goods: ',
      supplier: {
        columns: [
          'emissionSource',
          'supplierName',
          'supplierIntensityDataOrAbsoluteEmissions',
          'totalSpend',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    selector: {
      isActivityBranchNamedSpendData: true,
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with the capital goods your business purchases, you can use a combination of spend data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Spend category, total spend.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Spend category, supplier name, product description, supplier intensity data/absolute emissions, no. of units purchased, total spend.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData:
            'Emission source, product description, intensity data/absolute emissions, quantity of units purchased, total spend.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
      bottomContentsAll: bottomPurchasedCapitalAll,
      bottomContentsActivity: bottomPurchasedCapitalActivity,
    },
    useHookLoader: useGetCapitalGoodSpendDataSchema,
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedScope3Goods({
        questionFormatName: 'capital-goods',
      }),
    useQuestionBranchCustomLoader: () =>
      useFormatSharedScope3GoodsCustom({
        questionFormatName: 'capital-goods',
        sourceErrMsg: 'Please enter a valid source or identifier',
      }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data provided by suppliers for capital goods during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the amount spent in each of the following categories during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
